import type { ChecklistDefinition, DraftState, Question } from '../types';

export const questionTypes = {
  yes_no_na: 'Yes / No / N/A', yes_no: 'Yes / No', short_text: 'Short answer',
  long_text: 'Paragraph', number: 'Number', date: 'Date', time: 'Time',
  dropdown: 'Dropdown', multiple_choice: 'Choose one', checkbox: 'Confirmation', photo: 'Photo evidence',
};
export const activeSections = (d: ChecklistDefinition) => d.sections.filter(s => s.enabled !== false);
export const activeQuestions = (d: ChecklistDefinition) => activeSections(d).flatMap(s => s.groups.flatMap(g => g.questions.filter(q => q.enabled !== false)));
export function answered(q: Question, state: DraftState) {
  if (q.type === 'photo') return state.attachments.some(a => a.questionId === q.id);
  const value = state.answers[q.id];
  if (q.type === 'checkbox' && q.required) return value === true;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null && (typeof value !== 'number' || Number.isFinite(value));
}
export function missingRequired(d: ChecklistDefinition, state: DraftState) {
  return activeSections(d).flatMap((section, index) => section.groups.flatMap(g => g.questions
    .filter(q => q.enabled !== false && q.required && !answered(q, state))
    .map(question => ({ question, section, index }))));
}
export function isIssue(q: Question, value: unknown) {
  const trigger = q.issueWhen ?? (q.type === 'yes_no' || q.type === 'yes_no_na' ? 'No' : 'none');
  return trigger !== 'none' && value === trigger;
}
export const issueCount = (d: ChecklistDefinition, s: DraftState) => activeQuestions(d).filter(q => isIssue(q, s.answers[q.id])).length + s.issues.filter(i => i.observation.trim()).length;
export function answerLabel(q: Question, state: DraftState) {
  if (q.type === 'photo') {
    const n = state.attachments.filter(a => a.questionId === q.id).length;
    return n ? `${n} photo${n === 1 ? '' : 's'} attached` : 'No photos attached';
  }
  const v = state.answers[q.id];
  if (v === undefined || v === null || (typeof v === 'string' && !v.trim())) return 'Not answered';
  return typeof v === 'boolean' ? (v ? 'Confirmed' : 'Not confirmed') : String(v);
}
export function definitionErrors(value: unknown): string[] {
  const d = value as ChecklistDefinition | undefined;
  if (!d || !Array.isArray(d.sections)) return ['A checklist with sections is required.'];
  const errors: string[] = [];
  if (typeof d.title !== 'string' || d.title.trim().length < 3) errors.push('Use a checklist name with at least 3 characters.');
  const ids = new Set<string>();
  const unique = (id: string) => { if (!id || ids.has(id)) errors.push('Every section, group, and question needs a unique ID.'); ids.add(id); };
  let count = 0;
  for (const s of d.sections) {
    if (!s || !Array.isArray(s.groups)) return ['Every section must contain groups.'];
    unique(s.id);
    if (!s.title?.trim()) errors.push('Give every section a title.');
    if (!['checklist','declaration','guest_feedback','resident_feedback','issue_log'].includes(s.kind)) errors.push('Choose a valid section type.');
    for (const g of s.groups) {
      if (!g || !Array.isArray(g.questions)) return ['Every group must contain a question list.'];
      unique(g.id);
      if (!g.title?.trim()) errors.push(`Give the group in ${s.title} a title.`);
      for (const q of g.questions) {
        unique(q.id);
        if (!q.label?.trim()) errors.push(`Write a question in ${s.title}.`);
        if (!(q.type in questionTypes)) errors.push(`Choose an answer type for ${q.label}.`);
        if (q.issueWhen !== undefined && !['Yes','No','none'].includes(q.issueWhen)) errors.push(`Choose a valid issue rule for ${q.label}.`);
        if (['dropdown','multiple_choice'].includes(q.type)) {
          const options = q.options || [];
          if (options.length < 2 || options.some(o => typeof o !== 'string' || !o.trim())) errors.push(`${q.label}: add at least two non-empty options.`);
          if (new Set(options.map(o => o.trim().toLowerCase())).size !== options.length) errors.push(`${q.label}: remove duplicate options.`);
        }
        if (s.enabled !== false && q.enabled !== false) count++;
      }
    }
  }
  if (!activeSections(d).length || !count) errors.push('Add at least one enabled question in an enabled section.');
  return [...new Set(errors)];
}
export function sampleState(d: ChecklistDefinition): DraftState {
  return { status: 'local', details: { date: new Date().toISOString().slice(0,10), reportTime: '09:00', modName: 'Sample team member', resort: 'Fushifaru', inHouseGuests: '120', occupancy: '85' }, answers: Object.fromEntries(activeQuestions(d).filter(q => q.type !== 'photo').map(q => [q.id, q.type === 'checkbox' ? true : q.type === 'number' ? 24 : q.type === 'date' ? '2026-09-25' : q.type === 'time' ? '09:00' : q.type.startsWith('yes_no') ? 'Yes' : q.options?.[0] || 'Sample response'])), observations: {}, guestFeedback: [], residentFeedback: [], issues: [], attachments: [] };
}
export function copyDefinition(d: ChecklistDefinition, checklistId: string, title: string): ChecklistDefinition {
  return { ...structuredClone(d), checklistId, title, versionId: '', versionNumber: 0, sections: d.sections.map(s => ({ ...s, id: `section_${crypto.randomUUID()}`, groups: s.groups.map(g => ({ ...g, id: `group_${crypto.randomUUID()}`, questions: g.questions.map(q => ({ ...q, id: `question_${crypto.randomUUID()}` })) })) })) };
}
